import { useState } from 'react';
import { Sheet, Field, Input, Textarea, Select, AssigneeChips, PrimaryBtn, GhostBtn } from './UI';
import { TEAM, STATUSES, PRIORITIES, today } from '../utils/constants';

const blank = () => ({
  title: '', assignees: [], status: 'Action Required', priority: 'High',
  notes: '', nextSteps: '', completionNote: '', dateAdded: today(), dateResolved: null,
});

export function TaskForm({ initial, currentUser, onSave, onClose }) {
  const [form, setForm] = useState(() => initial ? { ...initial } : { ...blank(), assignees: currentUser ? [currentUser] : [] });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const stamp = () => {
    const d = new Date();
    const mo = d.getMonth() + 1;
    const dy = d.getDate();
    const yr = String(d.getFullYear()).slice(2);
    return ` — ${currentUser || 'Unknown'}, ${mo}/${dy}/${yr}`;
  };

  const progressTask = () => {
    if (!form.nextSteps.trim()) return;
    const archive = form.nextSteps.trim() + stamp();
    const newNotes = form.notes
      ? form.notes.trimEnd() + '\n\n' + archive
      : archive;
    setForm(f => ({ ...f, notes: newNotes, nextSteps: '' }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Task title is required.'); return; }
    setSaving(true);

    const s = stamp();
    const addStamp = (val, original) => {
      if (!val.trim()) return val;
      if (val === original) return val; // unchanged — no stamp
      const lastLine = val.trimEnd().split('\n').pop();
      if (lastLine.includes(' — ')) return val; // already stamped
      return val.trimEnd() + s;
    };

    const notes = addStamp(form.notes, initial?.notes || '');
    const nextSteps = addStamp(form.nextSteps, initial?.nextSteps || '');
    const completionNote = addStamp(form.completionNote, initial?.completionNote || '');

    const resolved = form.status === 'Complete' && !form.dateResolved ? today() : form.dateResolved;
    await onSave({ ...form, notes, nextSteps, completionNote, dateResolved: resolved });
    setSaving(false);
  };

  return (
    <Sheet
      title={initial ? 'Edit Task' : 'New Task'}
      onClose={onClose}
      footer={<><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Task'}</PrimaryBtn></>}
    >
      <Field label="Task Title *">
        <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Describe the task..." autoFocus />
      </Field>

      <Field label="Assigned To">
        <AssigneeChips team={TEAM} selected={form.assignees} onChange={v => set('assignees', v)} />
      </Field>

      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="Status">
          <Select value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={form.priority} onChange={e => set('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </Select>
        </Field>
      </div>

      <Field label="Notes / Description">
        <Textarea rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Background, context, details..." />
      </Field>

      <button
        type="button"
        onClick={progressTask}
        disabled={!form.nextSteps.trim()}
        style={{ background: form.nextSteps.trim() ? '#7c3aed' : '#e2e8f0', border: 'none', color: form.nextSteps.trim() ? '#fff' : '#94a3b8', borderRadius: 8, padding: '8px 14px', cursor: form.nextSteps.trim() ? 'pointer' : 'default', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}
        title="Copies Next Steps into Notes with your name and date, then clears Next Steps for a new entry"
      >
        ⬆ Progress Task
      </button>

      <Field label="Next Steps">
        <Textarea rows={3} value={form.nextSteps} onChange={e => set('nextSteps', e.target.value)} placeholder="What needs to happen next?" />
      </Field>

      <Field label="Completion Note">
        <Textarea rows={3} value={form.completionNote} onChange={e => set('completionNote', e.target.value)} placeholder="What was done? How was it resolved? (appears in weekly report)" />
      </Field>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 12px' }}>
        <input type="checkbox" id="mark-complete" checked={form.status === 'Complete'} onChange={e => set('status', e.target.checked ? 'Complete' : 'Action Required')} style={{ width: 18, height: 18, cursor: 'pointer' }} />
        <label htmlFor="mark-complete" style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', cursor: 'pointer' }}>Mark as Complete</label>
      </div>

      <Field label="Date Added">
        <Input type="date" value={form.dateAdded} onChange={e => set('dateAdded', e.target.value)} />
      </Field>

      {form.status === 'Complete' && (
        <Field label="Date Resolved">
          <Input type="date" value={form.dateResolved || today()} onChange={e => set('dateResolved', e.target.value)} />
        </Field>
      )}
    </Sheet>
  );
}
